import { useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { Logo } from "@/components/Logo";

function readNext(search: string): string {
  const params = new URLSearchParams(search);
  const next = params.get("next");
  if (!next) return "/";
  // Restrict to internal paths only.
  if (!next.startsWith("/") || next.startsWith("//")) return "/";
  return next;
}

export function Login() {
  const { user, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    return <Navigate to={readNext(location.search)} replace />;
  }

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      navigate(readNext(location.search), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Invalid email or password.");
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("Login failed.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={onSubmit}>
        <div className="auth-card__brand">
          <Logo size={28} />
          <div className="auth-card__title">
            Back<span className="accent">Stage</span>
          </div>
        </div>
        <p className="auth-card__sub">Sign in to the GPU control plane</p>

        <label className="auth-field">
          <span>Email</span>
          <input
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </label>
        <label className="auth-field">
          <span>Password</span>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>

        {error && <div className="auth-error">{error}</div>}

        <button
          type="submit"
          className="btn btn--primary auth-submit"
          disabled={submitting || !email || !password}
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>

        <div className="auth-card__footer">
          <Link to="/signup">Don't have an account? Apply for access</Link>
        </div>
      </form>
    </div>
  );
}
